<template>
  <net-popup-menu>
    <template #title>{{ $t("objectNames.impact") }}</template>
    
    <input-text-editor
      :label="$t('labels.description')"
      :value="ctx.relation.description"
      @update:value="ctx.updateKey('description', $event)"
    />

    <div class="column gap-4">
      <section>
        <div class="line-docanchor">
          <label class="label">{{ $t("labels.relationUpdateType") }}</label>
          <documentation-anchor :object="ctx.relation" prop="inverse" />
        </div>
        <input-radio
          :label="$t('labels.relationStandard')"
          :data="'not-inverse'"
          v-model:value="impactType"
        />
        <input-radio
          :label="$t('labels.relationInverse')"
          :data="'inverse'"
          v-model:value="impactType"
        />
        <input-radio
          :label="$t('relationUpdateTypes.none')"
          :data="'none'"
          v-model:value="impactType"
        />
      </section>

      <template v-if="ctx.relation.updateType !== 'none'">

        <section
          v-if="targetOnlyRelation"
        >
          <!-- MAX INFLUENCE -->
          <div class="line-docanchor">
            <label class="label">{{ $t("labels.impactStrength") }}</label>
            <documentation-anchor :object="ctx.relation" prop="maxInfluence" />
          </div>
          <input-strength
            :strength="curveMaxInfluence"
            :strength-presets="relationStrengthPresets"
            :unit="ctx.endItem?.unit"
            @update:strength="updateMaxInfluence"
          />
        </section>
        <input-number
          v-else
          :value="curveMaxInfluence"
          :label="$t('labels.relationStrength')"
          step="any"
          @update:value="updateMaxInfluence($event)"
        />

        <section
          v-if="
            curveMaxInfluence > 0 ||
            !targetOnlyRelation ||
            ctx.relation.curveType == 'formula'
          "
        >
          <div class="line-docanchor">
            <label class="label">{{ $t("labels.influenceCurve") }}</label>
            <documentation-anchor :object="ctx.relation" prop="curve" />
          </div>
          <input-curve
            :value="startItemValue"
            :x-min="startItemMinMax.minValue"
            :x-max="startItemMinMax.maxValue"
            :point-list="ctx.relation.curvePoints"
            :type="ctx.relation.curveType"
            :fixed-start="true"
            :formula="ctx.relation.formula"
            :color-areas-visible="ctx.startItem?.type === 'target' && ctx.endItem?.type === 'target'"
            class="mt-1"
            @change="onGraphChange"
            @update:formula="ctx.updateKey('formula', $event)"
            @hovervalue="updateCurveHover($event)"
          />
          <p>
            {{ $t("labels.xAxis") }}{{ $t(":") }}
            {{
              $t("labels.valueOfX", [
                ctx.startItem?.title || $t("labels.unnamed")
              ])
            }}<br />
            {{ $t("labels.yAxis") }}{{ $t(":") }}
            {{
              $t("labels.influenceOnX", [
                ctx.endItem?.title || $t("labels.unnamed")
              ])
            }}
          </p>

          
          <i18n-t
            keypath="texts.influenceCurveExample"
            tag="p"
            scope="global"
          >
            <template #startElement>
              <em>{{ ctx.startItem?.title || $t("labels.unnamed") }}</em>
            </template>
            <template v-if="startItemUnit" #startUnit
              > {{ startItemUnit }}
            </template>
            <template #endElement>
              <em>{{ ctx.endItem?.title || $t("labels.unnamed") }}</em>
            </template>
            <template v-if="ctx.endItem?.unit" #endUnit
              > {{ ctx.endItem?.unit }}
            </template>
            <template #startValueA>
              {{ curveHover.x2 }}
            </template>
            <template #startValueB> {{ curveHover.x }}</template>
            <template #impulseValue>
              {{ $n(curveHover.y - curveHover.y2 || 0, "displaySign") }}
            </template>
          </i18n-t>
        </section>
      </template>
    </div>

    <div v-if="ctx.relation.updateType !== 'none'">
      <h3 class="label mt-0">{{ $t("labels.delay") }}</h3>
      <div class="line-docanchor">
        <div>
          <input-radio
            v-slot="{ id: delayInputId }"
            v-model:value="relationDelayed"
            :data="false"
          >
            <label :for="delayInputId" class="pv-1">
              <strong>{{ $t("labels.noDelay") }}</strong>
              <div class="mt-1">{{ $t("texts.effectInNextRound") }}</div>
            </label>
          </input-radio>
  
          <input-radio
            v-slot="{ selected, id: delayInputId }"
            v-model:value="relationDelayed"
            :data="true"
          >
            <div v-if="selected" class="pv-1">
              <label :for="delayInputId">
                <strong>{{ $t("labels.delay") }}:</strong>
              </label>
              <div class="line_flex items-center mt-1 gap-1">
                <input-number
                  ref="inputRelationDelayField"
                  style="width: 4rem"
                  :disabled="!selected"
                  :input-class="selected ? [] : ['pointer-events-none']"
                  :value="ctx.relation.delay + 1"
                  :min="2"
                  @update:value="ctx.updateKey('delay', $event - 1)"
                />
                {{ $t(`roundUnits.${roundUnit}`, ctx.relation.delay + 1) }}
              </div>
            </div>
            <label v-else :for="delayInputId" class="pv-1">
              <strong>{{ $t("actions.setDelay") }}</strong>
            </label>
          </input-radio>
        </div>
  
        <documentation-anchor :object="ctx.relation" prop="delay" class="self-start" />
      </div>
    </div>

    <button
      class="button button_secondary mv-2"
      type="button"
      @click="ctx.delete()"
    >
      {{ $t("actions.deleteRelation") }}
    </button>
  </net-popup-menu>
</template>

<script setup lang="ts">
import { computed, reactive, nextTick, ref } from "vue";
import { useI18n } from "vue-i18n";
import { showConfirm } from "@core/ui/alert/alert";

import NetPopupMenu from '@editor/net/NetPopupMenu.vue';
import InputTextEditor from '@core/input/TextEditor.vue';
import InputNumber from "@core/input/InputNumber.vue";
import InputRadio from '@core/input/RadioButton.vue';
import InputCurve from '@editor/items/relation/InputCurve.vue';
import InputStrength from '@editor/items/relation/InputStrength.vue';
import DocumentationAnchor from "@editor/ui/DocumentationAnchor.vue";

import { useRelation } from "./useRelation";
import { useRelationStore } from "./relations.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useIndexStore } from "../index/indices.store";
import { useElements } from "../element/elements";

import type { GraphPoint } from "./GraphPoint";
import type { CurveFormType, RelationItem } from "./RelationItem";

const props = defineProps<{
  relationId: number;
}>();

defineEmits<{
  (e: "select", item: RelationItem): void;
}>();

// const features = useFeatures();
const { t } = useI18n();
const inputRelationDelayField = ref<InstanceType<typeof InputNumber>>();

const relationStore = useRelationStore();
const settingsStore = useSettingsStore();
const indexStore = useIndexStore();

const ctx = useRelation(() => props.relationId);

const curveHover = reactive({ x: 0, y: 0, x2: 0, y2: 0 });

const relationStrengthPresets = computed(() => {
  return settingsStore.relationStrengthPresets;
});

const curveMaxInfluence = computed(() => {
  return (
    ctx.graphModel?.absoluteMaxInfluence ?? ctx.relation.maxInfluence
  );
});

const inverted = computed({
  get() {
    return ctx.relation.inverse;
  },
  set(val: boolean) {
    let current = inverted.value;
    if (val === current) return;

    relationStore.invertCurve({
      id: props.relationId
    });
  }
});


const impactType = computed({
  get() {
    return ctx.relation.updateType === "none" ? "none" : (ctx.relation.inverse ? "inverse" : "not-inverse");
  },
  set(value: "none" | "inverse" | "not-inverse") {
    if (value == "none") {
      ctx.updateKey("updateType", "none");
    } else {
      const inverted = value == "inverse";
      if (inverted != ctx.relation.inverse) {
        relationStore.invertCurve({
          id: props.relationId
        });
      } else if (ctx.relation.updateType == "none") {
        ctx.updateKey("updateType", "change");
      }
    }
  }
});

/**
 * Is the relation only between targets, no resources?
 */
const targetOnlyRelation = computed(() => {
  return (
    ctx.relation.startItemType === "target" &&
    ctx.relation.endItemType === "target"
  );
});


const startItemValue = computed(() => {
  if (!ctx.startItem) return 0;
  if ("value" in ctx.startItem) return ctx.startItem.value;

  return indexStore.values[ctx.startItem.id];
});

const elementValues = computed(() => useElements().elementValues);

const startItemMinMax = computed(() => {
  if (!ctx.startItem) return { minValue: 0, maxValue: 0 };
  return elementValues.value[ctx.startItem.uid];
});

const startItemUnit = computed(() => {
  if (!ctx.startItem) return "";
  if ("unit" in ctx.startItem) return ctx.startItem.unit;

  return "";
});

function onGraphChange(data: { points: GraphPoint[]; type: CurveFormType }) {
  relationStore.updateCurve({
    id: props.relationId,
    curvePoints: data.points,
    curveType: data.type
  });
}

function updateMaxInfluence(value: number) {
  const relation = ctx.updateKey("maxInfluence", value);
  relationStore.updatePointBounds({ id: relation.id });
}

function updateCurveHover(newData: {
  x: number;
  y: number;
  x2: number;
  y2: number;
}) {
  curveHover.x = newData.x;
  curveHover.y = newData.y;
  curveHover.x2 = newData.x2;
  curveHover.y2 = newData.y2;
}

// =====================
// DELAY


const roundUnit = computed(() => {
  return settingsStore.simulationRoundUnit;
});

const relationDelayed = computed({
  get() {
    return ctx.relation.delay > 0;
  },
  async set(delayed: boolean) {
    if (ctx.relation.delay > 0 && delayed) return;
    if (ctx.relation.delay == 0 && !delayed) return;

    if (delayed) {
      ctx.updateKey("delay", Math.max(1, ctx.relation.delay));
      nextTick(() => {
        inputRelationDelayField.value?.focus();
      });
    } else {
      if (await showConfirm(t("questions.confirmRemovingDelay"))) {
        ctx.updateKey("delay", 0);
      }
    }
  }
});
</script>